import { Link } from 'react-router-dom'
import { LANDING_PLANS } from '@/constants/plans'
import { cn } from '@/lib/cn'

/**
 * Landing pública pre-registro de MapeoData.
 *
 * Muestra los 3 planes DEC-026 hardcodeados desde
 * `src/constants/plans.ts` (decisión KISS YAGNI integrada T-004: cero
 * fetch en landing, optimiza SEO + LCP). Si futuro pricing dinámico
 * público → endpoint `/api/v1/public/plans` con DEC propia.
 *
 * Diseño minimalista mientras el CEO no produzca branding visual definitivo:
 * texto-only "MapeoData" en blue.700 como logo, paleta heredada de Metriclave
 * en azul (DEC-005 blue.500 = #2563eb Tailwind default).
 */
export default function Landing() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="text-2xl font-bold text-blue-700">
            MapeoData
          </Link>
          <nav className="flex gap-4">
            <Link
              to="/login"
              className="text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              Iniciar sesión
            </Link>
            <Link
              to="/registro"
              className="rounded-md bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600"
            >
              Empezar gratis
            </Link>
          </nav>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="mx-auto max-w-6xl px-6 py-16 text-center">
          <h1 className="mb-6 text-5xl font-bold text-gray-900">
            Extrae leads B2B con cobertura geográfica precisa
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            Encuentra negocios por categoría profesional y zona administrativa.
            Exporta a CSV, Excel o JSON. Sin tarjeta para empezar.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/registro"
              className="rounded-md bg-blue-500 px-6 py-3 text-base font-semibold text-white hover:bg-blue-600"
            >
              Crear cuenta gratis
            </Link>
            <Link
              to="/login"
              className="rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-700 hover:border-blue-500 hover:text-blue-600"
            >
              Ya tengo cuenta
            </Link>
          </div>
        </section>

        {/* Planes hardcodeados (DEC-026, decisión KISS YAGNI T-004) */}
        <section className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="mb-2 text-center text-3xl font-bold text-gray-900">
            Planes simples y transparentes
          </h2>
          <p className="mb-12 text-center text-gray-600">
            Empieza gratis. Escala cuando lo necesites.
          </p>
          <div className="grid gap-6 md:grid-cols-3">
            {LANDING_PLANS.map((plan) => (
              <div
                key={plan.code}
                className={cn(
                  'rounded-lg border-2 border-gray-200 bg-white p-6',
                  plan.code === 'starter' && 'border-blue-500 shadow-lg',
                )}
              >
                {plan.code === 'starter' && (
                  <div className="mb-3 inline-block rounded-full bg-blue-500 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                    Más popular
                  </div>
                )}
                <h3 className="mb-2 text-xl font-bold text-gray-900">{plan.name_es}</h3>
                <p className="mb-4 text-sm text-gray-600">{plan.description_es}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">
                    {plan.monthly_price_eur === 0
                      ? 'Gratis'
                      : `${plan.monthly_price_eur} €`}
                  </span>
                  {plan.monthly_price_eur !== 0 && (
                    <span className="text-sm text-gray-500">/mes</span>
                  )}
                </div>
                <ul className="mb-6 space-y-2 text-sm text-gray-700">
                  <li>
                    <strong>{plan.monthly_leads_quota}</strong> leads al mes
                  </li>
                  <li>
                    Hasta <strong>{plan.single_job_max_leads}</strong> leads por extracción
                  </li>
                  <li>Export CSV {plan.features.export_csv ? '✓' : '—'}</li>
                  <li>Export Excel {plan.features.export_excel ? '✓' : '—'}</li>
                  <li>Presets sectoriales {plan.features.presets_sectoriales ? '✓' : '—'}</li>
                </ul>
                <Link
                  to="/registro"
                  className={cn(
                    'block rounded-md py-2 text-center text-sm font-semibold',
                    plan.code === 'starter'
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'border border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600',
                  )}
                >
                  {plan.code === 'free' ? 'Empezar gratis' : 'Elegir plan'}
                </Link>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-8 text-center text-sm text-gray-500">
          © 2026 MapeoData. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  )
}
